import { useFormContext } from 'react-hook-form';

const FORM_SECTIONS = [
  {
    label: 'General Information',
    weight: 1,
    // `status` is set by the admin and isn't editable by the FFL - it is usually still empty on
    // a complete profile, so requiring it kept the percentage below 100.
    fields: ['logo'],
  },
  {
    label: 'Store Details',
    weight: 2,
    // The trial end date (saved as `ffl_expiry_date`) is deliberately not counted: it is set by the
    // admin and the FFL has no field to enter it, so requiring it kept a complete profile below 100%.
    fields: [
      'personalName',
      'business_name',
      'ffl_no',
      'mobile',
      'email',
      'website',
      'payment_type',
      'is_shipping',
    ],
  },
  {
    label: 'Transfer Fees',
    weight: 1,
    fields: ['long_gun_fee', 'handgun_fee', 'nfa_fee', 'other_fee'],
  },
  {
    label: 'Address',
    weight: 1,
    fields: [],
    customCheck: (valuesObj: any) => {
      const address = valuesObj?.address;
      if (!address || typeof address !== 'object') return 0;

      return isFieldFilled(address?.add1) ||
        isFieldFilled(address?.zipcode) ||
        isFieldFilled(address?.city)
        ? 1
        : 0;
    },
  },
  {
    label: 'Working Days',
    weight: 1,
    fields: [],
    customCheck: (valuesObj: any) => {
      const wd = valuesObj?.working_days;
      if (!wd) return 0;

      return Object.keys(wd).length > 0 ? 1 : 0;
    },
  },
  {
    label: 'Store Description',
    weight: 1,
    fields: ['bio'],
  },
  {
    label: 'Social Media',
    weight: 1,
    anyOneRequired: true,
    fields: [
      'instagram_profile',
      'youtube_profile',
      'facebook_profile',
      'twitter_profile',
      'google_business_account',
      'yellow_page',
      'yelp',
      'facebook',
      'trustpilot',
      'yelp_profile',
      'yellow_page_profile',
    ],
  },
  {
    label: 'Banner Images',
    weight: 1,
    fields: ['banner_image_url'],
  },
  {
    label: 'Payment Gateways',
    weight: 1,
    fields: [],
    // One connected gateway is enough. A saved gateway carries `status` (the profile only lists the
    // ones that are connected) - it has no `connected` flag, so checking only that never counted.
    customCheck: (valuesObj: any) => {
      const gateways = valuesObj?.payment_gateways;
      if (!Array.isArray(gateways)) return 0;

      return gateways.some((g: any) => g?.connected === true || g?.status === true) ? 1 : 0;
    },
  },
  {
    label: 'Shipping',
    weight: 1,
    fields: [],
    // One connected shipping carrier is enough - the same "active" a carrier card shows as Connected.
    customCheck: (valuesObj: any) => {
      const carriers = valuesObj?.shipengine_carriers;
      if (!Array.isArray(carriers)) return 0;

      return carriers.some((c: any) => c?.status === 'active' && c?.connected !== false) ? 1 : 0;
    },
  },
];

function isFieldFilled(value: any): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  return String(value).trim().length > 0;
}

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

export interface SectionCompletion {
  label: string;
  filled: boolean;
  weight: number;
}

export interface FormCompletion {
  percentage: number;
  sections: SectionCompletion[];
  filledCount: number;
  totalCount: number;
}

export function calculateFormCompletion(valuesObj: any): FormCompletion {
  let totalWeight = 0;
  let filledWeight = 0;

  const sections: SectionCompletion[] = [];

  for (const section of FORM_SECTIONS) {
    totalWeight += section.weight;

    let sectionFilled = false;

    if (section.customCheck) {
      sectionFilled = section.customCheck(valuesObj) === 1;
    } else if (section.anyOneRequired) {
      sectionFilled = section.fields.some((field) =>
        isFieldFilled(getNestedValue(valuesObj, field))
      );
    } else {
      sectionFilled = section.fields.every((field) =>
        isFieldFilled(getNestedValue(valuesObj, field))
      );
    }

    if (sectionFilled) {
      filledWeight += section.weight;
    }

    sections.push({
      label: section.label,
      filled: sectionFilled,
      weight: section.weight,
    });
  }

  const percentage = totalWeight > 0 ? Math.round((filledWeight / totalWeight) * 100) : 0;

  return {
    percentage,
    sections,
    filledCount: sections.filter((s) => s.filled).length,
    totalCount: sections.length,
  };
}

export const normalizeFFLDetails = (details: any): any => {
  if (!details) return {};
  return {
    status: details.status,
    logo: details.logo,
    personalName: `${details?.user?.first_name ?? ''} ${details?.user?.last_name ?? ''}`.trim(),
    business_name: details.business_name,
    ffl_no: details.ffl_no,
    mobile: details.mobile,
    email: details.email ?? details?.user?.email,
    website: details.website,
    payment_type: details.payment_type,
    is_shipping: details.is_shipping,
    long_gun_fee: details.long_gun_fee,
    handgun_fee: details.handgun_fee,
    nfa_fee: details.nfa_fee,
    other_fee: details.other_fee,
    // The API keeps the address at the top level (add1, zipcode, city...) and has no `address`
    // object - only the form builds one from those same fields. Build it here when the saved
    // data has none, or the Address section could never count.
    address: details.address ?? {
      add1: details.add1,
      zipcode: details.zipcode,
      city: details.city ?? details.city_name,
    },
    working_days: details.working_days ?? details.working_hours,
    bio: details.bio,
    instagram_profile: details.instagram_profile,
    youtube_profile: details.youtube_profile,
    facebook_profile: details.facebook_profile,
    twitter_profile: details.twitter_profile,
    google_business_account: details.google_business_account,
    yellow_page: details.yellow_page,
    yelp: details.yelp,
    facebook: details.facebook,
    trustpilot: details.trustpilot,
    yelp_profile: details.yelp_profile,
    yellow_page_profile: details.yellow_page_profile,
    banner_image_url: details.banner_images || details.banner_image_url,
    payment_gateways: details.payment_gateways,
    shipengine_carriers: details.shipengine_carriers,
  };
};

// Every form field the completion % reads. Values are looked up by name below, never by position -
// a positional lookup read `payment_gateways` from the wrong index, so that section never counted
// in the badges inside the cards and they showed 10 points lower than the profile header.
const COMPLETION_FIELDS = [
  'status',
  'logo',
  'personalName',
  'business_name',
  'ffl_no',
  'mobile',
  'email',
  'website',
  'payment_type',
  'is_shipping',
  'long_gun_fee',
  'handgun_fee',
  'nfa_fee',
  'other_fee',
  'address',
  'working_days',
  'bio',
  'instagram_profile',
  'youtube_profile',
  'facebook_profile',
  'twitter_profile',
  'google_business_account',
  'yellow_page',
  'yelp',
  'facebook',
  'trustpilot',
  'yelp_profile',
  'yellow_page_profile',
  'banner_image_url',
  'payment_gateways',
  'shipengine_carriers',
] as const;

export function useFormCompletion(): FormCompletion {
  const context = useFormContext() as any;
  if (!context) {
    return { percentage: 0, sections: [], filledCount: 0, totalCount: 0 };
  }
  const { watch } = context;

  const values = watch([...COMPLETION_FIELDS]);
  const valuesObj = Object.fromEntries(
    COMPLETION_FIELDS.map((field, index) => [field, values[index]])
  );

  return calculateFormCompletion(valuesObj);
}
